import React from "react";
import { Bell, CheckCircle, X } from "./Icons";

export interface Toast {
  id: string;
  title: string;
  message: string;
  type: "info" | "success" | "error";
  onClick?: () => void;
  duration: number;
  remainingTime: number;
}

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
  isPaused: boolean;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove, isPaused }) => {
  const progress = (toast.remainingTime / toast.duration) * 100;

  return (
    <div
      onClick={() => {
        if (toast.onClick) toast.onClick();
        onRemove(toast.id);
      }}
      className={`pointer-events-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl rounded-xl p-4 flex items-start gap-4 cursor-pointer animate-in slide-in-from-right-full fade-in duration-300 transition-all hover:scale-[1.02] active:scale-95 group overflow-hidden relative`}
    >
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400`}
      >
        {toast.type === "info" ? <Bell size={20} /> : <CheckCircle size={20} />}
      </div>
      <div className="flex-1 min-w-0 py-0.5">
        <h4 className="text-[15px] font-bold text-slate-900 dark:text-white leading-tight">
          {toast.title}
        </h4>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
          {toast.message}
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(toast.id);
        }}
        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1"
      >
        <X size={16} />
      </button>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100 dark:bg-slate-800">
        <div
          className={`h-full transition-all duration-100 ease-linear bg-indigo-500`}
          style={{
            width: `${progress}%`,
          }}
        />
      </div>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
  onPause: (paused: boolean) => void;
  isPaused: boolean;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onRemove,
  onPause,
  isPaused,
}) => {
  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none"
      onMouseEnter={() => onPause(true)}
      onMouseLeave={() => onPause(false)}
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={onRemove}
          isPaused={isPaused}
        />
      ))}
    </div>
  );
};
